/**
 * The page editor's op queue (plan 3.3, 7.3; the WP5 server note is the
 * contract). Platform-free: the web and native editing surfaces and the
 * tests drive the same class.
 *
 * - Ops wait for an 800 ms debounce and go out in one `page-edits` batch
 *   (at most 200 ops a request), or at once on blur, Done, close and app
 *   background (`flush`). A duplicate is sent at once too, because the copy
 *   only gets an id (and becomes editable) after the editor reloads.
 * - Every save of the session carries the same `editSessionId` (a UUID), so
 *   the server squashes them into one version; each save names the version
 *   it was made against (`baseVersionId`), and the version it answers is the
 *   next base.
 * - A text change replaces an unsent text change of the same element (the
 *   frame posts the whole block each time); an attribute change replaces the
 *   unsent change of the same attribute. Nothing is merged across a
 *   duplicate of that element, which copies the text as it is then.
 * - After a save the server says which ids of the base changed (`ids`): the
 *   frame is told with `renumber` (`onSaved`), and ops queued meanwhile are
 *   mapped through it (an element that is gone drops its ops).
 * - Failures: a network or server error is retried twice (1 s, then 3 s),
 *   then reads "Couldn't save. Try again." with the ops kept. A 409 stops the
 *   session (Reload); a 422 drops the refused batch and asks for a reload so
 *   the frame shows what was saved; a 429 keeps the ops for Try again.
 * - `markNextSave('suggestion')` (the suggestions card applying through the
 *   open editor) marks the batch that carries those ops `via: 'suggestion'`
 *   for analytics; a retried batch keeps the mark.
 */
import type { ArtifactEditVia, PageEditOp, PageEditsResult } from '@tabbio/types';
import { classifySaveError, type SaveFailure } from './save-errors';
import { saveStateForFailure, type EditorSaveState } from './save-state';

export const PAGE_EDIT_DEBOUNCE_MS = 800;
export const PAGE_EDIT_BATCH_MAX = 200;
export const PAGE_EDIT_RETRY_DELAYS_MS: readonly number[] = [1_000, 3_000];

export type PageEditSaveRequest = {
  baseVersionId: string;
  editSessionId: string;
  ops: PageEditOp[];
  /** Set when the batch applies a suggestion (the `suggest-apply` analytics action). */
  via?: ArtifactEditVia;
};

export type PageEditQueueDeps = {
  save: (request: PageEditSaveRequest) => Promise<PageEditsResult>;
  /**
   * A batch was stored: renumber the frame, show findings, refresh the file.
   * `baseVersionId` is the version the batch was made against.
   */
  onSaved?: (result: PageEditsResult, sent: readonly PageEditOp[], baseVersionId: string) => void;
  /** The frame no longer matches the stored page: load `editor-source` again. */
  onReloadNeeded?: (reason: 'duplicate' | 'invalid') => void;
  onStateChange?: (state: EditorSaveState) => void;
  /** A save failed for good (after retries); the failure the header shows. */
  onFailure?: (failure: SaveFailure) => void;
  setTimeout?: (callback: () => void, ms: number) => unknown;
  clearTimeout?: (handle: unknown) => void;
  debounceMs?: number;
  retryDelaysMs?: readonly number[];
};

function isTextOp(op: PageEditOp): op is Extract<PageEditOp, { op: 'setText' | 'setInline' }> {
  return op.op === 'setText' || op.op === 'setInline';
}

/**
 * Where a new op replaces an unsent one, or -1: a text op replaces the last
 * text op of its element, an attribute op the last op on the same attribute,
 * unless the element was duplicated after it (the copy took that text).
 */
function supersededIndex(pending: readonly PageEditOp[], next: PageEditOp): number {
  if (!isTextOp(next) && next.op !== 'setAttr') return -1;
  for (let index = pending.length - 1; index >= 0; index -= 1) {
    const op = pending[index];
    if (op.id !== next.id) continue;
    if (op.op === 'duplicate' || op.op === 'remove') return -1;
    if (isTextOp(next) && isTextOp(op)) return index;
    if (next.op === 'setAttr' && op.op === 'setAttr' && op.name === next.name) return index;
    // A replaced image resets its alt text: an older alt stays in order.
    if (next.op === 'setAttr' && op.op === 'replaceImage') return -1;
  }
  return -1;
}

/** Ops of the base version mapped through the ids the save answered. */
export function remapPageEditOps(
  ops: readonly PageEditOp[],
  ids: Readonly<Record<string, string | null>> | undefined,
): PageEditOp[] {
  if (!ids || Object.keys(ids).length === 0) return [...ops];
  const out: PageEditOp[] = [];
  for (const op of ops) {
    if (!Object.prototype.hasOwnProperty.call(ids, op.id)) {
      out.push(op);
      continue;
    }
    const next = ids[op.id];
    if (next) out.push({ ...op, id: next });
  }
  return out;
}

export class PageEditQueue {
  private pending: PageEditOp[] = [];
  private base: string;
  private inFlight: Promise<boolean> | null = null;
  private timer: unknown = null;
  private retries = 0;
  private stopped = false;
  private reloadAfterIdle: 'duplicate' | null = null;
  private viaNext: ArtifactEditVia | null = null;
  private waiters: ((ok: boolean) => void)[] = [];
  private state: EditorSaveState = { status: 'idle' };
  private readonly setTimer: (callback: () => void, ms: number) => unknown;
  private readonly clearTimer: (handle: unknown) => void;
  private readonly debounceMs: number;
  private readonly retryDelays: readonly number[];

  constructor(
    baseVersionId: string,
    readonly editSessionId: string,
    private readonly deps: PageEditQueueDeps,
  ) {
    this.base = baseVersionId;
    this.setTimer = deps.setTimeout ?? ((callback, ms) => setTimeout(callback, ms));
    this.clearTimer = deps.clearTimeout ?? ((handle) => clearTimeout(handle as ReturnType<typeof setTimeout>));
    this.debounceMs = deps.debounceMs ?? PAGE_EDIT_DEBOUNCE_MS;
    this.retryDelays = deps.retryDelaysMs ?? PAGE_EDIT_RETRY_DELAYS_MS;
  }

  /** The version the next save is made against. */
  get baseVersionId(): string {
    return this.base;
  }

  get saveState(): EditorSaveState {
    return this.state;
  }

  /** Unsent ops, in order (a copy). */
  get queued(): PageEditOp[] {
    return [...this.pending];
  }

  /** Changes not stored yet: queued, or on their way. */
  hasUnsaved(): boolean {
    return this.pending.length > 0 || this.inFlight !== null;
  }

  /** Stopped by a 409: nothing more is sent until `reset`. */
  get isStopped(): boolean {
    return this.stopped;
  }

  /** The next batch sent applies a suggestion (call it before enqueueing the suggestion's ops). */
  markNextSave(via: ArtifactEditVia) {
    if (!this.stopped) this.viaNext = via;
  }

  enqueue(op: PageEditOp) {
    if (this.stopped) return;
    const replaced = supersededIndex(this.pending, op);
    if (replaced >= 0) this.pending.splice(replaced, 1);
    this.pending.push(op);
    if (op.op === 'duplicate') {
      this.reloadAfterIdle = 'duplicate';
      void this.flush();
      return;
    }
    // A save on its way sends what queued meanwhile when it lands.
    if (!this.inFlight) {
      this.setState({ status: 'dirty' });
      this.schedule();
    }
  }

  /**
   * Send everything now (blur, Done, close, background, Try again). Resolves
   * true once the queue is empty and stored, false when a save failed.
   */
  flush(): Promise<boolean> {
    this.cancelTimer();
    if (this.stopped) return Promise.resolve(false);
    if (!this.inFlight && this.pending.length === 0) {
      this.maybeReload();
      return Promise.resolve(this.state.status !== 'failed');
    }
    const done = new Promise<boolean>((resolve) => this.waiters.push(resolve));
    if (!this.inFlight) void this.run();
    return done;
  }

  /** Try again after a failure or the hourly cap. */
  retry(): Promise<boolean> {
    this.retries = 0;
    return this.flush();
  }

  /**
   * A fresh start on a reloaded page (after a reload, a conflict or a
   * duplicate): new base, nothing queued. The session id stays, so saves
   * keep squashing into the same version.
   */
  reset(baseVersionId: string) {
    this.cancelTimer();
    this.pending = [];
    this.base = baseVersionId;
    this.stopped = false;
    this.retries = 0;
    this.reloadAfterIdle = null;
    this.viaNext = null;
    this.settle(true);
    this.setState({ status: 'idle' });
  }

  dispose() {
    this.cancelTimer();
    this.stopped = true;
    this.settle(false);
  }

  private schedule() {
    this.cancelTimer();
    this.timer = this.setTimer(() => {
      this.timer = null;
      void this.run();
    }, this.debounceMs);
  }

  private cancelTimer() {
    if (this.timer !== null) {
      this.clearTimer(this.timer);
      this.timer = null;
    }
  }

  private setState(next: EditorSaveState) {
    this.state = next;
    this.deps.onStateChange?.(next);
  }

  private settle(ok: boolean) {
    const waiters = this.waiters;
    this.waiters = [];
    for (const resolve of waiters) resolve(ok);
  }

  private maybeReload() {
    if (this.reloadAfterIdle && !this.inFlight && this.pending.length === 0) {
      const reason = this.reloadAfterIdle;
      this.reloadAfterIdle = null;
      this.deps.onReloadNeeded?.(reason);
    }
  }

  private run(): Promise<boolean> {
    if (this.inFlight) return this.inFlight;
    if (this.stopped || this.pending.length === 0) {
      this.settle(!this.stopped);
      this.maybeReload();
      return Promise.resolve(!this.stopped);
    }
    const batch = this.pending.slice(0, PAGE_EDIT_BATCH_MAX);
    this.pending = this.pending.slice(batch.length);
    const via = this.viaNext;
    this.viaNext = null;
    this.setState({ status: 'saving' });
    const request: PageEditSaveRequest = {
      baseVersionId: this.base,
      editSessionId: this.editSessionId,
      ops: batch,
      ...(via ? { via } : {}),
    };
    const saving = this.deps.save(request).then(
      (result) => {
        this.inFlight = null;
        return this.onSuccess(result, batch);
      },
      (error: unknown) => {
        this.inFlight = null;
        return this.onError(error, batch, via);
      },
    );
    this.inFlight = saving;
    return saving;
  }

  private onSuccess(result: PageEditsResult, batch: PageEditOp[]): boolean {
    if (this.stopped) return false;
    this.retries = 0;
    const base = this.base;
    this.base = result.version.id;
    this.pending = remapPageEditOps(this.pending, result.ids);
    this.deps.onSaved?.(result, batch, base);
    this.afterSave();
    return true;
  }

  /** What follows a stored batch: the rest of the queue, or idle. */
  private afterSave() {
    if (this.stopped) return;
    if (this.pending.length > 0) {
      if (this.waiters.length > 0 || this.reloadAfterIdle) {
        void this.run();
      } else {
        this.setState({ status: 'dirty' });
        this.schedule();
      }
      return;
    }
    this.setState({ status: 'saved' });
    this.settle(true);
    this.maybeReload();
  }

  private onError(error: unknown, batch: PageEditOp[], via: ArtifactEditVia | null = null): boolean {
    if (this.stopped) return false;
    const failure = classifySaveError(error);
    if (failure.kind === 'retryable' && this.retries < this.retryDelays.length) {
      const delay = this.retryDelays[this.retries] ?? 1_000;
      this.retries += 1;
      this.pending = [...batch, ...this.pending];
      if (via) this.viaNext = via;
      this.timer = this.setTimer(() => {
        this.timer = null;
        void this.run();
      }, delay);
      return false;
    }
    this.retries = 0;
    if (failure.kind === 'conflict' || failure.kind === 'gone' || failure.kind === 'disabled') {
      // Nothing more can be stored against this base.
      this.stopped = true;
      this.pending = [];
      this.reloadAfterIdle = null;
      this.cancelTimer();
    } else if (failure.kind === 'invalid') {
      // The refused batch cannot succeed; the frame shows it, so reload.
      this.reloadAfterIdle = null;
      void Promise.resolve().then(() => this.deps.onReloadNeeded?.('invalid'));
    } else {
      // Kept for Try again (the hourly cap, or retries spent).
      this.pending = [...batch, ...this.pending];
      if (via) this.viaNext = via;
    }
    this.setState(saveStateForFailure(failure));
    this.deps.onFailure?.(failure);
    this.settle(false);
    return false;
  }
}
